/** Utilitaires de texte et de dates (interface en français, RG-70). */

/** Minuscules sans accents ni ponctuation superflue : recherche tolérante (RG-21). */
export function normalize(s: string): string {
  return s
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .toLowerCase()
    .replace(/[’'`]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

/** « Albert Camus, Pierre-Louis Rey » → ['Albert Camus', 'Pierre-Louis Rey']. */
export function parseAuthors(text: string): string[] {
  return text
    .split(/[;,\n]/)
    .map((a) => a.replace(/\s+/g, ' ').trim())
    .filter((a) => a !== '')
}

export function joinAuthors(authors: string[]): string {
  return authors.join(', ')
}

const DAY = 24 * 60 * 60 * 1000

/** Nombre de jours entiers écoulés depuis d. */
export function daysSince(d: Date, now = new Date()): number {
  return Math.max(0, Math.floor((now.getTime() - d.getTime()) / DAY))
}

/** « depuis 27 jours », « depuis aujourd'hui ». */
export function sinceLabel(d: Date, now = new Date()): string {
  const n = daysSince(d, now)
  if (n === 0) return 'depuis aujourd’hui'
  if (n === 1) return 'depuis hier'
  return `depuis ${n} jours`
}

/** « 12 septembre » (année ajoutée si différente de l'année en cours). */
export function formatDay(d: Date, now = new Date()): string {
  const opts: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'long' }
  if (d.getFullYear() !== now.getFullYear()) opts.year = 'numeric'
  return new Intl.DateTimeFormat('fr-FR', opts).format(d)
}

/** « 14 mars 2026 ». */
export function formatDate(d: Date): string {
  return new Intl.DateTimeFormat('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' }).format(d)
}

/** « 14/03/2026 » pour les tableaux. */
export function formatShortDate(d: Date): string {
  return new Intl.DateTimeFormat('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' }).format(d)
}

/** Seuil du badge « Emprunté depuis longtemps », en semaines (RG-37). */
export const LOAN_REMINDER_WEEKS = (() => {
  const n = Number(import.meta.env.VITE_LOAN_REMINDER_WEEKS)
  return Number.isFinite(n) && n > 0 ? n : 4
})()

/** RG-37 : calculé à l'affichage, aucune relance active. */
export function isLongLoan(borrowedAt: Date | undefined, now = new Date(), weeks = LOAN_REMINDER_WEEKS): boolean {
  return borrowedAt !== undefined && now.getTime() - borrowedAt.getTime() > weeks * 7 * DAY
}

/** « 1 livre », « 347 livres ». */
export function plural(n: number, one: string, many: string): string {
  return `${n.toLocaleString('fr-FR')} ${n > 1 ? many : one}`
}

/** Normalise un ISBN (tirets, espaces) ; '' si invalide. */
export function normalizeIsbn(s: string): string {
  const n = s.toUpperCase().replace(/[\s-]/g, '')
  if (/^\d{13}$/.test(n) || /^\d{9}[\dX]$/.test(n)) return n
  return ''
}

/** Vrai si l'EAN-13 est un ISBN valide (préfixe 978/979 et clé de contrôle). */
export function isValidIsbn13(s: string): boolean {
  if (!/^97[89]\d{10}$/.test(s)) return false
  const sum = [...s.slice(0, 12)].reduce((acc, c, i) => acc + Number(c) * (i % 2 === 0 ? 1 : 3), 0)
  return (10 - (sum % 10)) % 10 === Number(s[12])
}

/** Lisibilité : préfixe EAN séparé (« 978-2070360248 ») ; les groupes internes dépendent de l'éditeur. */
export function displayIsbn(s: string): string {
  return s.length === 13 ? `${s.slice(0, 3)}-${s.slice(3)}` : s
}

export function roleLabel(role: string): string {
  switch (role) {
    case 'member':
      return 'Membre'
    case 'editor':
      return 'Éditeur'
    case 'superadmin':
      return 'Superadmin'
    default:
      return 'En attente'
  }
}
